import {
	Calendar,
	Car,
	FileCheck,
	FileText,
	ListChecks,
	type LucideIcon,
	Plane,
	Search,
	Shield,
	TrendingUp,
} from "lucide-react";
import { GoalCategoryTypeEnum } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";

export type RealizationStep = {
	icon: LucideIcon;
	title: string;
	subtitle: string;
};

export type RealizationContent = {
	kicker: string;
	description: string;
	steps: RealizationStep[];
};

export const useRealizationContent = (
	category: GoalCategoryTypeEnum,
): RealizationContent => {
	const { t } = useResource();
	const variants = Resources.Goals.Form.Realization.Variants;

	const variantByCategory: Record<GoalCategoryTypeEnum, RealizationContent> = {
		[GoalCategoryTypeEnum.House]: {
			kicker: t(variants.Home.kicker),
			description: t(variants.Home.description),
			steps: [
				{
					icon: FileText,
					title: t(variants.Home.Steps.preApproval.title),
					subtitle: t(variants.Home.Steps.preApproval.subtitle),
				},
				{
					icon: Search,
					title: t(variants.Home.Steps.search.title),
					subtitle: t(variants.Home.Steps.search.subtitle),
				},
				{
					icon: FileCheck,
					title: t(variants.Home.Steps.documents.title),
					subtitle: t(variants.Home.Steps.documents.subtitle),
				},
			],
		},
		[GoalCategoryTypeEnum.Car]: {
			kicker: t(variants.Car.kicker),
			description: t(variants.Car.description),
			steps: [
				{
					icon: FileText,
					title: t(variants.Car.Steps.preApproval.title),
					subtitle: t(variants.Car.Steps.preApproval.subtitle),
				},
				{
					icon: Car,
					title: t(variants.Car.Steps.pick.title),
					subtitle: t(variants.Car.Steps.pick.subtitle),
				},
				{
					icon: FileCheck,
					title: t(variants.Car.Steps.documents.title),
					subtitle: t(variants.Car.Steps.documents.subtitle),
				},
			],
		},
		[GoalCategoryTypeEnum.Pension]: {
			kicker: t(variants.Retirement.kicker),
			description: t(variants.Retirement.description),
			steps: [
				{
					icon: Calendar,
					title: t(variants.Retirement.Steps.start.title),
					subtitle: t(variants.Retirement.Steps.start.subtitle),
				},
				{
					icon: Shield,
					title: t(variants.Retirement.Steps.strategy.title),
					subtitle: t(variants.Retirement.Steps.strategy.subtitle),
				},
			],
		},
		[GoalCategoryTypeEnum.Student]: {
			kicker: t(variants.Education.kicker),
			description: t(variants.Education.description),
			steps: [
				{
					icon: FileText,
					title: t(variants.Education.Steps.enroll.title),
					subtitle: t(variants.Education.Steps.enroll.subtitle),
				},
				{
					icon: Calendar,
					title: t(variants.Education.Steps.plan.title),
					subtitle: t(variants.Education.Steps.plan.subtitle),
				},
			],
		},
		[GoalCategoryTypeEnum.Basic]: {
			kicker: t(variants.Custom.kicker),
			description: t(variants.Custom.description),
			steps: [
				{
					icon: Calendar,
					title: t(variants.Custom.Steps.schedule.title),
					subtitle: t(variants.Custom.Steps.schedule.subtitle),
				},
				{
					icon: ListChecks,
					title: t(variants.Custom.Steps.tasks.title),
					subtitle: t(variants.Custom.Steps.tasks.subtitle),
				},
			],
		},
		[GoalCategoryTypeEnum.Vacation]: {
			kicker: t(variants.Vacation.kicker),
			description: t(variants.Vacation.description),
			steps: [
				{
					icon: Plane,
					title: t(variants.Vacation.Steps.book.title),
					subtitle: t(variants.Vacation.Steps.book.subtitle),
				},
				{
					icon: FileCheck,
					title: t(variants.Vacation.Steps.plan.title),
					subtitle: t(variants.Vacation.Steps.plan.subtitle),
				},
			],
		},
		[GoalCategoryTypeEnum.Rent]: {
			kicker: t(variants.Rent.kicker),
			description: t(variants.Rent.description),
			steps: [
				{
					icon: Calendar,
					title: t(variants.Rent.Steps.start.title),
					subtitle: t(variants.Rent.Steps.start.subtitle),
				},
				{
					icon: TrendingUp,
					title: t(variants.Rent.Steps.strategy.title),
					subtitle: t(variants.Rent.Steps.strategy.subtitle),
				},
			],
		},
	};

	return variantByCategory[category];
};
